import { z } from "zod";
import {
	type DiagnosticSnapshot,
	diagnosticSnapshotSchema,
} from "./diagnostic-ring-buffer";

export enum DefectClientPlatform {
	Desktop = "desktop",
	Webapp = "webapp",
}

export enum DefectEnvironment {
	Prod = "prod",
	Proto = "proto",
	LocalDev = "local-dev",
}

const RESERVED_TOP_LEVEL_SEGMENTS = new Set([
	"whiteboards",
	"home",
	"recents",
	"shared",
	"drafts",
	"templates",
	"profile",
	"settings",
	"login",
	"logout",
	"cli",
	"onboarding",
	"referral",
	"admin",
	"pricing",
	"billing",
	"invite",
	"auth",
	"callback",
]);

export const routeEntityIdsSchema = z.object({
	boardId: z.string().nullable(),
	cardId: z.string().nullable(),
});

export type RouteEntityIds = z.infer<typeof routeEntityIdsSchema>;

export const defectUserSummarySchema = z.object({
	id: z.string(),
	email: z.string(),
	displayName: z.string(),
});

export type DefectUserSummary = z.infer<typeof defectUserSummarySchema>;

export const defectPosthogLinksSchema = z.object({
	sessionId: z.string().nullable(),
	distinctId: z.string().nullable(),
	replayUrl: z.string().nullable(),
	recordingActive: z.boolean(),
});

export type DefectPosthogLinks = z.infer<typeof defectPosthogLinksSchema>;

export const defectSentryLinksSchema = z.object({
	replayId: z.string().nullable(),
	feedbackEventId: z.string().nullable(),
	replayUrl: z.string().nullable().default(null),
	feedbackUrl: z.string().nullable().default(null),
});

export type DefectSentryLinks = z.infer<typeof defectSentryLinksSchema>;

export const defectAttachmentMetaSchema = z.object({
	name: z.string(),
	contentType: z.string(),
	sizeBytes: z.number(),
});

export type DefectAttachmentMeta = z.infer<typeof defectAttachmentMetaSchema>;

const SENTRY_ORG_BASE_URL = "https://illumi-one.sentry.io";
const SENTRY_WEBAPP_PROJECT_ID = "4508736763527168";

export function buildSentryUrls(params: {
	replayId: string | null;
	feedbackEventId: string | null;
	reportId: string;
}): { replayUrl: string | null; feedbackUrl: string | null } {
	const replayUrl = params.replayId
		? `${SENTRY_ORG_BASE_URL}/replays/${encodeURIComponent(params.replayId)}/?project=${SENTRY_WEBAPP_PROJECT_ID}`
		: null;
	const feedbackUrl = params.feedbackEventId
		? `${SENTRY_ORG_BASE_URL}/feedback/?project=${SENTRY_WEBAPP_PROJECT_ID}&query=${encodeURIComponent(`report_id:${params.reportId}`)}`
		: null;
	return { replayUrl, feedbackUrl };
}

export const defectReportPayloadSchema = z.object({
	reportId: z.string(),
	occurredAt: z.string(),
	environment: z.nativeEnum(DefectEnvironment),
	client: z.nativeEnum(DefectClientPlatform),
	appVersion: z.string(),
	url: z.string(),
	pathname: z.string(),
	boardId: z.string().nullable(),
	cardId: z.string().nullable(),
	note: z.string(),
	user: defectUserSummarySchema.nullable(),
	attachments: z.array(defectAttachmentMetaSchema).default([]),
	viewport: z.object({
		width: z.number(),
		height: z.number(),
		devicePixelRatio: z.number(),
	}),
	userAgent: z.string(),
	language: z.string(),
	timezone: z.string(),
	online: z.boolean(),
	posthog: defectPosthogLinksSchema,
	sentry: defectSentryLinksSchema,
	diagnostics: diagnosticSnapshotSchema,
});

export type DefectReportPayload = z.infer<typeof defectReportPayloadSchema>;

export function extractRouteEntityIds(
	pathname: string,
	search = "",
): RouteEntityIds {
	const segments = pathname.split("/").filter(Boolean);
	const params = new URLSearchParams(search);

	if (segments[0] === "whiteboards") {
		const boardIdFromPath = segments[1] ?? null;
		const boardIdFromQuery = params.get("id");
		const cardIdFromQuery = params.get("cardId");
		return routeEntityIdsSchema.parse({
			boardId: boardIdFromPath ?? boardIdFromQuery ?? null,
			cardId: cardIdFromQuery ?? null,
		});
	}

	if (
		segments.length === 1 &&
		segments[0] &&
		!RESERVED_TOP_LEVEL_SEGMENTS.has(segments[0].toLowerCase())
	) {
		return routeEntityIdsSchema.parse({
			boardId: params.get("boardId") ?? null,
			cardId: segments[0],
		});
	}

	return routeEntityIdsSchema.parse({
		boardId: params.get("boardId") ?? null,
		cardId: params.get("cardId") ?? null,
	});
}

export function detectDefectEnvironment(
	hostname = typeof window !== "undefined" ? window.location.hostname : "",
	apiHost = String(
		import.meta.env.VITE_GRAPHQL_URL ?? import.meta.env.VITE_API_URL ?? "",
	),
): DefectEnvironment {
	const combined = `${hostname} ${apiHost}`.toLowerCase();
	if (combined.includes("proto.illumi.sh")) {
		return DefectEnvironment.Proto;
	}
	if (combined.includes("illumi.one")) {
		return DefectEnvironment.Prod;
	}
	return DefectEnvironment.LocalDev;
}

export interface BuildDefectReportInput {
	reportId: string;
	occurredAt?: string;
	note: string;
	user: DefectUserSummary | null;
	attachments?: DefectAttachmentMeta[];
	posthog: DefectPosthogLinks;
	sentry: {
		replayId: string | null;
		feedbackEventId: string | null;
		replayUrl?: string | null;
		feedbackUrl?: string | null;
	};
	diagnostics: DiagnosticSnapshot;
}

export function buildDefectReportPayload(
	input: BuildDefectReportInput,
): DefectReportPayload {
	const hasWindow = typeof window !== "undefined";
	const pathname = hasWindow ? window.location.pathname : "/";
	const search = hasWindow ? window.location.search : "";
	const url = hasWindow ? window.location.href : "http://localhost/";
	const { boardId, cardId } = extractRouteEntityIds(pathname, search);
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
	const sentryUrls = buildSentryUrls({
		replayId: input.sentry.replayId,
		feedbackEventId: input.sentry.feedbackEventId,
		reportId: input.reportId,
	});

	return defectReportPayloadSchema.parse({
		reportId: input.reportId,
		occurredAt: input.occurredAt ?? new Date().toISOString(),
		environment: detectDefectEnvironment(),
		client:
			hasWindow && Boolean(window.electronAPI?.isElectron)
				? DefectClientPlatform.Desktop
				: DefectClientPlatform.Webapp,
		appVersion: String(import.meta.env.VITE_APP_VERSION ?? "0.0.0"),
		url,
		pathname,
		boardId,
		cardId,
		note: input.note.trim(),
		user: input.user,
		attachments: input.attachments ?? [],
		viewport: {
			width: hasWindow ? window.innerWidth : 0,
			height: hasWindow ? window.innerHeight : 0,
			devicePixelRatio: hasWindow ? window.devicePixelRatio || 1 : 1,
		},
		userAgent:
			typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
		language: typeof navigator !== "undefined" ? navigator.language : "en",
		timezone,
		online: typeof navigator !== "undefined" ? navigator.onLine : true,
		posthog: input.posthog,
		sentry: {
			replayId: input.sentry.replayId,
			feedbackEventId: input.sentry.feedbackEventId,
			replayUrl: input.sentry.replayUrl ?? sentryUrls.replayUrl,
			feedbackUrl: input.sentry.feedbackUrl ?? sentryUrls.feedbackUrl,
		},
		diagnostics: input.diagnostics,
	});
}
